import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getCurrentUser, updateUser, UpdateUserRequest } from '../apis/userApi';

export const useUser = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Query for fetching current user
  const {
    data: user,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getCurrentUser(token);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Mutation for updating user
  const updateUserMutation = useMutation({
    mutationFn: async (data: UpdateUserRequest) => {
      const token = await getAccessTokenSilently();
      return updateUser(data, token);
    },
    onSuccess: () => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: error => {
      console.error('Error updating user:', error);
    },
  });

  const error =
    queryError?.message || updateUserMutation.error?.message || null;

  return {
    user,
    loading,
    updating: updateUserMutation.isPending,
    error,
    updateUser: updateUserMutation.mutateAsync,
    refresh: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  };
};
